import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Building2, Lock, Globe, ArrowRight, ArrowLeft, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { t, language, toggleLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error(language === 'ar' ? 'كلمة السر يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(language === 'ar' ? 'كلمات السر غير متطابقة' : 'Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setDone(true);
      toast.success(language === 'ar' ? 'تم إعادة تعيين كلمة السر بنجاح' : 'Password reset successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'الرابط غير صالح أو منتهي الصلاحية' : 'Invalid or expired reset link'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20 flex items-center justify-center px-6" data-testid="reset-password-page">
        <div className="max-w-md w-full text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-heading font-bold text-primary mb-2">
            {language === 'ar' ? 'رابط غير صالح' : 'Invalid Link'}
          </h2>
          <p className="text-muted-foreground mb-6">
            {language === 'ar' ? 'رابط إعادة التعيين غير صالح أو مفقود.' : 'The reset link is invalid or missing.'}
          </p>
          <Link to="/forgot-password" data-testid="go-to-forgot-btn"
            className="inline-block px-6 py-3 bg-gradient-to-r from-accent to-accent/90 text-white font-semibold rounded-lg hover:shadow-lg transition-all">
            {language === 'ar' ? 'طلب رابط جديد' : 'Request a New Link'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20 flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <Link to="/login" className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-login">
            {language === 'ar' ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
            {language === 'ar' ? 'تسجيل الدخول' : 'Login'}
          </Link>
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors bg-white rounded-lg border border-border"
            data-testid="language-toggle"
          >
            <Globe size={18} />
            {language === 'ar' ? 'English' : 'عربي'}
          </button>
        </div>

        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-accent to-accent/70 rounded-xl flex items-center justify-center">
              <Building2 size={28} className="text-white" />
            </div>
            <span className="text-3xl font-heading font-black bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">{t('appName')}</span>
          </Link>
          <h1 className="text-3xl font-heading font-bold text-primary mb-2">
            {language === 'ar' ? 'إعادة تعيين كلمة السر' : 'Reset Password'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'أدخل كلمة السر الجديدة' : 'Enter your new password'}
          </p>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-border shadow-lg">
          {done ? (
            <div className="text-center py-6" data-testid="reset-success">
              <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
              <h2 className="text-xl font-heading font-bold text-primary mb-2">
                {language === 'ar' ? 'تم بنجاح!' : 'Success!'}
              </h2>
              <p className="text-muted-foreground mb-6">
                {language === 'ar' ? 'تم إعادة تعيين كلمة السر. يمكنك الآن تسجيل الدخول.' : 'Password has been reset. You can now login.'}
              </p>
              <Link to="/login" data-testid="go-to-login-btn"
                className="inline-block px-6 py-3 bg-gradient-to-r from-accent to-accent/90 text-white font-semibold rounded-lg hover:shadow-lg transition-all">
                {language === 'ar' ? 'تسجيل الدخول' : 'Login'}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'ar' ? 'كلمة السر الجديدة' : 'New Password'}
                </label>
                <div className="relative">
                  <Lock size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="new-password-input"
                    className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-new-password-visibility"
                    className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors`}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'ar' ? 'تأكيد كلمة السر' : 'Confirm Password'}
                </label>
                <div className="relative">
                  <Lock size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                    className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    data-testid="toggle-confirm-password-visibility"
                    className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors`}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="w-full py-3 bg-gradient-to-r from-accent to-accent/90 text-white font-semibold rounded-lg hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 transition-all duration-200"
              >
                {loading
                  ? (language === 'ar' ? 'جاري التحديث...' : 'Updating...')
                  : (language === 'ar' ? 'إعادة تعيين كلمة السر' : 'Reset Password')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
